import { useCallback, useEffect, useState } from 'react';

import { useFishCropImages } from '@restDataAccess/orbitOneGateway';

import { nextBackgroundImagePaths } from './fishObservationGallery.functions.ts';

type UseFullResolutionPrefetchArgs = {
  galleryPaths: string[];
  thumbnailsReady: boolean;
};

/**
 * Fetches original crops in the background after thumbnails have settled.
 * Opening a card or changing the lightbox slide can jump that path to the front of the queue.
 */
export const useFullResolutionPrefetch = ({
  galleryPaths,
  thumbnailsReady,
}: UseFullResolutionPrefetchArgs) => {
  const [queuedPaths, setQueuedPaths] = useState<string[]>([]);
  const activePaths = queuedPaths.filter(path => galleryPaths.includes(path));
  const { urls, statusByPath } = useFishCropImages({ paths: activePaths });

  // After thumbs settle, enqueue full-resolution crops two at a time so the lightbox is warm.
  useEffect(() => {
    setQueuedPaths(currentPaths => {
      const nextPaths = nextBackgroundImagePaths({
        allPaths: galleryPaths,
        queuedPaths: currentPaths,
        statusByPath,
        thumbnailsReady,
      });
      return areSamePaths(currentPaths, nextPaths) ? currentPaths : nextPaths;
    });
  }, [galleryPaths, statusByPath, thumbnailsReady]);

  const prioritizePath = useCallback((path: string) => {
    setQueuedPaths(currentPaths =>
      currentPaths.includes(path) ? currentPaths : [path, ...currentPaths]
    );
  }, []);

  return { urls, statusByPath, prioritizePath };
};

const areSamePaths = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((path, index) => path === right[index]);
